import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import Select from 'react-select'
import ImgPieceBlank from '../../../../components/icon/ImgPieceBlank'
import StripePaiementStepper from './StripePaiementStepper'
import {
  createStripePaymentIntents,
  getPaiementMontant,
  PaiementMontantSociete,
  StripePaiementSociete,
} from '../../../../components/requests'
import {useLocation} from 'react-router'
import {Tooltip} from 'react-tooltip'
import {toast} from "react-toastify";

// Codes partagés avec App\Enum\ModePaiement côté backend.
const MODE_CB = 'cb'
const MODE_VIREMENT = 'virement'
const MODE_EN_COMPTE = 'en_compte'
const MODE_VAD = 'vad'
const MODE_ESPECE = 'espece'
const MODE_CB_TPE = 'cb_tpe'

const API_URL = process.env.REACT_APP_API_URL
const imgPOT_url = `${API_URL}/photos/POT/`

const PaymentResumeModal = (props) => {
  const payerRef = useRef<(() => void) | null>(null)
  const {
    updatedAdresseLivInfo,
    updatedAdresseFactInfo,
    pieces,
    setCheckedPieces,
    onValidate,
    onPaiementReussi,
    btnCancelAndCloseRef,
    clientParametrage,
    clientAdresses,
    dataClient,
    livraisonSelectedValue,
    facturationSelectedValue,
    handleSelectedLivChange,
    handleSelectedFactChange,
    handleSelectedDepotPiece,
    total,
    setTotal,
    totalLibelleFraisPort,
    selectedOption,
    setSelectedOption,
    adresseDepots,
    selectedDepotsByPiece,
    source,
    demandeId,
    clientId,
    isClientSide = true, // côté page suggestion de pièce (SuggestionPage)
    retraitDepot = false,
    setRetraitDepot,
    commentaireInterne,
    setCommentaireInterne,
    montants,
    setMontants = () => {},
  } = props

  // Montants dus, calculés par le backend, indépendamment du moyen de règlement.
  // Paiements Stripe préparés par le backend : un par société du panier.
  const [paiements, setPaiements] = useState<StripePaiementSociete[]>([])
  const [chargementPaiements, setChargementPaiements] = useState<boolean>(false)
  const [erreurPaiement, setErreurPaiement] = useState<string | null>(null)
  const [paiementEnCours, setPaiementEnCours] = useState<boolean>(false)
  const [tousPayes, setTousPayes] = useState<boolean>(false)
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [cgvChecked, setCgvChecked] = useState<boolean>(false)

  const [activeAccount, setActiveAccount] = useState<boolean>(false)
  const [btnSubmitText, setBtnSubmitText] = useState<string>(isClientSide ? 'Payer' : 'Valider')
  const adresseLivraison = clientAdresses?.filter((adresse) => adresse.livraison === true)
  const adresseFacturation = clientAdresses?.filter((adresse) => adresse.facturation === true)
  const location = useLocation()

  const isFirstRender = useRef(true)

  useEffect(
    function checkIfClientHasBankAccount() {
      setActiveAccount(clientParametrage?.some((cP) => cP.enCompte === true))
    },
    [clientParametrage]
  )

  // CB Stripe et virement : ouverts à tous. « En compte » : client en compte et non bloqué.
  const modesDisponibles = useMemo(() => {
    const modes = [
      {value: MODE_VIREMENT, label: 'Virement bancaire'},
    ]

    // Côté Client
    if (isClientSide) {
      modes.push({value: MODE_CB, label: 'Carte bancaire'})
      if (activeAccount && !dataClient?.messageBloquant) {
        modes.push(
          {value: MODE_EN_COMPTE, label: 'Validation en compte'},
        )
      }
    } else {
      // Côté Televendeur (VAD + CB TPE)
      modes.push(
        {value: MODE_ESPECE, label: 'Espèces'},
        {value: MODE_VAD, label: 'VAD'},
        {value: MODE_CB_TPE, label: 'CB TPE'},
      )

      if (dataClient?.professionnel && activeAccount && !dataClient?.messageBloquant) {
        modes.push({value: MODE_EN_COMPTE, label: 'Validation en compte'})
      }
    }

    return modes
  }, [activeAccount, dataClient?.messageBloquant])
  // Aucun mode retenu, ou mode devenu indisponible (client bloqué entre-temps) :
  // on retombe sur la CB, seule option toujours ouverte.
  // Mode par défaut: côté client (MODE_CB) / côté Televendeur (MODE_EN_COMPTE)
  useEffect(function selectionModeParDefautTelevendeur() {
    const enCompte = modesDisponibles?.filter((mode) => mode?.value === MODE_EN_COMPTE)
    if (enCompte?.length === 0) {
      setSelectedOption('')
    } else {
      if (isClientSide) return
      setSelectedOption(MODE_EN_COMPTE)
    }
  }, [modesDisponibles])

  useEffect(
    function selectionnerModeParDefaut() {
      if (!isClientSide) return
      if (modesDisponibles.some((mode) => mode.value === selectedOption)) return

      appliquerMode(MODE_CB)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isClientSide, modesDisponibles, selectedOption]
  )

  // Le montant affiché est celui calculé par le backend, qui sert aussi de base
  // aux PaymentIntent : l'affichage ne peut pas diverger de l'encaissement.
  let montantAPayer = 0
  if (retraitDepot) {
    //si retrait dépot y a pas de transports
    montantAPayer = montants?.length > 0
      ? montants?.reduce((somme, montant) => somme + (montant?.montantTtc ?? 0), 0)
      : total?.piece ?? 0
  } else {
    montantAPayer = montants?.length > 0
      ? montants?.reduce((somme, montant) => somme + (montant?.montantTtc ?? 0), 0)
      : (total?.piece ?? 0) + (total?.transport ?? 0)
  }
  const transportAPayer = montants?.length > 0
    ? montants?.reduce((somme, montant) => somme + (montant?.montantTransportTtc ?? 0), 0)
    : total?.transport ?? 0

  const idsPiecesSelectionnees = useCallback(
    () =>
      (pieces ?? [])
        .map((piece) => (isClientSide ? piece?.id : piece?.idPiecePropose))
        .filter(Boolean),
    [pieces]
  )

  const chargerMontants = useCallback(async () => {
    if (!isClientSide) return

    const idsPieces = idsPiecesSelectionnees()
    if (idsPieces.length === 0) {
      setMontants([])
      return
    }

    try {
      const res = await getPaiementMontant(demandeId, clientId, idsPieces, source, retraitDepot)
      setMontants(res?.data?.montants ?? [])
    } catch (error) {
      // Sans montant serveur, le récapitulatif retombe sur le calcul local.
      setMontants([])
    }
  }, [isClientSide, idsPiecesSelectionnees, demandeId, clientId, source, retraitDepot])

  const chargerPaiements = useCallback(async () => {
    if (!isClientSide) return

    const idsPieces = idsPiecesSelectionnees()
    if (idsPieces.length === 0) {
      setPaiements([])
      return
    }

    setChargementPaiements(true)
    setErreurPaiement(null)

    try {
      const res = await createStripePaymentIntents(
        demandeId,
        clientId,
        idsPieces,
        source,
        retraitDepot
      )
      setPaiements(res?.data?.paiements ?? [])
    } catch (error: any) {
      setPaiements([])
      setErreurPaiement(
        error?.response?.data?.error ?? 'La préparation du paiement en ligne a échoué.'
      )
    } finally {
      setChargementPaiements(false)
    }
  }, [isClientSide, idsPiecesSelectionnees, demandeId, clientId, source, retraitDepot])

  useEffect(() => {
    //relance les montants et le payement quand on change de mode de livraison
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setPaiements([])
    chargerMontants()
  }, [retraitDepot])

  useEffect(
    function suivreOuvertureDuModal() {
      if (!isClientSide) return

      const modal = document.getElementById('modal_payment')
      if (!modal) return

      const onShown = () => {
        setModalVisible(true)
        chargerMontants()
      }
      const onHidden = () => {
        setModalVisible(false)
        setMontants([])
        setPaiements([])
        setErreurPaiement(null)
        setPaiementEnCours(false)
      }

      modal.addEventListener('shown.bs.modal', onShown)
      modal.addEventListener('hidden.bs.modal', onHidden)

      return () => {
        modal.removeEventListener('shown.bs.modal', onShown)
        modal.removeEventListener('hidden.bs.modal', onHidden)
      }
    },
    [isClientSide, chargerMontants]
  )

  // Les PaymentIntent ne sont créés que si le client règle par CB, et seulement
  // à l'ouverture du modal (et non à chaque case cochée), pour ne pas multiplier
  // les intents côté Stripe.
  useEffect(
    function preparerPaiementCb() {
      if (!modalVisible) return
      if (selectedOption !== MODE_CB) return
      if (paiements.length > 0 || chargementPaiements || erreurPaiement) return

      chargerPaiements()
    },
    [
      isClientSide,
      modalVisible,
      selectedOption,
      paiements.length,
      chargementPaiements,
      erreurPaiement,
      chargerPaiements,
      retraitDepot,
    ]
  )

  // Transfromation du donnnée en value/label pour l'option de react-select
  const transformedDataLiv = clientAdresses
    ?.filter((adresse) => adresse.livraison === true)
    ?.map((item) => ({
      value: item.id,
      label: item.libelle ?? item.adresse,
    }))

  // Transfromation du donnnée en value/label pour l'option de react-select
  const transformedDataFact = clientAdresses
    ?.filter((adresse) => adresse.facturation === true)
    ?.map((item) => ({
      value: item.id,
      label: item.libelle ?? item.adresse,
    }))

  // Transfromation du donnnée en value/label pour l'option de react-select
  // Insertion de la valeur vide
  const transformedDataDepot = [
    {value: null, label: ''},
    ...(adresseDepots ?? [])?.map((item) => ({
      value: item.id,
      label: item.nom,
    }))
  ]
  // Vérification si c'est un retrait dépôt et que l'adresse de dépôt selectionnée n'est pas la valeur vide selectionnée par défaut
  const haveNoSelectedDepot = (retraitDepot && Object.values(selectedDepotsByPiece).some(
    (value) => value === null
  ))

  const removePieceOnPanier = (idPiece) => {
    setCheckedPieces((prevArray) => prevArray.filter((p) => p !== idPiece))
  }

  // Total de prix des pièce en ttc et du prix de transport de la pièce
  const getTotalToPaid = (pieces) => {
    const total_piece_ttc = pieces?.reduce(
      (total, piece) => total + ((isClientSide ? piece?.prixProposeeTTC : piece?.prixVenteProposeeTtc) || 0),
      0
    )
    const total_transport = retraitDepot
        ? 0
        : pieces?.reduce(
            (total, piece) =>
                total +
                ((piece?.fraisDePort !== 0
                        ? piece?.fraisDePort
                        : piece?.fraisPortPieceEntre) *
                    1.2 || 0),
            0
        ); // frais de port toujours 20% TVA

    setTotal({
      piece: Number(parseFloat(total_piece_ttc).toFixed(2)),
      transport: Number(parseFloat(total_transport).toFixed(2)) + totalLibelleFraisPort,
    })
  }

  useEffect(
    function calculTotalOfPiece() {
      getTotalToPaid(pieces)
    },
    [pieces, retraitDepot]
  )

  const getLink = (value: string): string => {
    switch (value) {
      case 'AUTO':
        return 'https://www.surplusautos.fr/conditions-generales-de-vente-en-ligne-aux-consommateurs-surplus-autos'
      case 'MOTO':
        return 'https://www.surplusmotos.com/cgv-piecessurplusmotos-grandpublic'
      case 'INDUSTRIE':
        return 'https://surplus-poidslourd.com/conditions-generales-de-ventes'
      default:
        return 'https://www.surplusautos.fr/conditions-generales-de-vente-en-ligne-aux-consommateurs-surplus-autos'
    }
  }

  const handleChangeRetraitDepot = () => {
    setRetraitDepot(!retraitDepot)
  }

  const renderPieceList = () => {
    if (pieces?.length > 0) {
      return pieces?.map((item, index) => (
        <div key={index} className={'row gap-5 mb-3 border rounded p-2 align-items-center mx-1'}>
          <div className={'col'}>
            <div className={'d-flex align-items-center gap-6'}>
              {item?.images?.length !== 0 ? (
                <div>
                  <img
                    src={`${item.isPOT ? imgPOT_url : 'https://koena.fr/photos/pieces/'}${
                      item.images[0]
                    }`}
                    className='w-100 rounded'
                    width='64'
                    height='40'
                    alt={`${item?.nomPieceProposee}`}
                  />
                </div>
              ) : (
                <ImgPieceBlank width={64} height={64} />
              )}
              <div>
                <div className={'fw-bold text-gray-800'}>
                  {item?.nomPieceProposee ?? item?.nomTypePieceProposee}
                </div>
                <div>{item?.reference ?? item?.refConstructeur}</div>
                <div className={'mt-2'}>
                  <div className={'text-info fw-bold fs-6'}>
                    {item?.prixPropose ?? item?.prixVenteProposeeHt ?? 0} € HT /{' '}
                    {item?.prixProposeeTTC ?? item?.prixVenteProposeeTtc} € TTC
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={'col'}>
            <div className={'d-flex align-items-center text-muted'}>
              {item?.marqueVehicle ?? item?.marque} {item?.modeleVehicle ?? item?.modele}
            </div>
          </div>
          <div className={'col-2'}>
            <div className={'text-center'}>
              <span className={'btn btn-icon btn-bg-light btn-sm'}>
                <i
                  className='ki-duotone ki-trash fs-2x text-danger cursor-pointer'
                  onClick={() =>
                    removePieceOnPanier(isClientSide ? item?.id : item?.idPiecePropose)
                  }
                >
                  <span className='path1'></span>
                  <span className='path2'></span>
                  <span className='path3'></span>
                  <span className='path4'></span>
                  <span className='path5'></span>
                </i>
              </span>
            </div>
          </div>
        </div>
      ))
    } else {
      return (
        <div
          className={'mb-3 border rounded p-2 align-items-center mr-3 text-center text-muted w-100'}
        >
          Aucune pièce séléctionnée
        </div>
      )
    }
  }

  const renderAdresseList = () => {
    return (
      <>
        <div className={'mb-6'}>
          <div className={'mb-1'}>
            <div className={'d-flex gap-2 mb-3 justify-content-between align-items-center'}>
              <div className={'d-flex align-items-center gap-2'}>
                <i className='ki-duotone ki-address-book fs-2x text-primary'>
                  <span className='path1'></span>
                  <span className='path2'></span>
                  <span className='path3'></span>
                </i>
                <label className={'text-gray-800 fs-4'}>Adresse de facturation</label>
              </div>
              <div style={{maxWidth: '200px'}}>
                <Select
                  className='react-select-styled react-select-solid react-select-lg min-w-200px'
                  classNamePrefix='react-select'
                  fieldName='facturation'
                  placeholder='Adresse de facturation'
                  options={transformedDataFact}
                  value={facturationSelectedValue}
                  onChange={(e) => handleSelectedFactChange(e)}
                  isDisabled={adresseFacturation?.length <= 1 ? 'true' : ''}
                />
              </div>
            </div>
          </div>
          <div className={'ms-3 border rounded p-2 ps-3'}>
            <span className='d-block text-muted fs-bold'>
              {dataClient?.professionnel
                ? dataClient?.nomSociete
                : [dataClient?.nom, dataClient?.prenom].filter(Boolean).join(' ')}
            </span>
            <span className={'d-block text-muted'}>{updatedAdresseFactInfo?.adresseFac}</span>
            <span className={'d-block text-muted'}>
              {updatedAdresseFactInfo?.complementAdresseFac}
            </span>
            <span className={'d-block text-muted'}>
              {updatedAdresseFactInfo?.cPFac} {updatedAdresseFactInfo?.villeFac}
            </span>
            <span className={'d-block text-muted'}>{updatedAdresseFactInfo?.paysFac}</span>
          </div>
        </div>

        {/* début affichage du switch retrait depot ou livraison */}
        <div>
          <div className='d-flex justify-content-start gap-3 mb-3'>
            <h5
              className={`cursor-pointer ${!retraitDepot ? 'text-primary' : 'text-gray-400'}`}
              onClick={() => setRetraitDepot(false)}
            >
              Adresse de livraison
            </h5>
            <div className='form-check form-switch form-check-custom form-check-solid mt-n2 mx-2'>
              <input
                id='retraitDeport'
                type='checkbox'
                name='retraitDeport'
                className='form-check-input h-20px w-35px cursor-pointer'
                onChange={() => handleChangeRetraitDepot()}
                checked={retraitDepot}
              />
            </div>
            <h5
              className={`cursor-pointer ${retraitDepot ? 'text-primary' : 'text-gray-400'}`}
              onClick={() => setRetraitDepot(true)}
            >
              Retrait dépôt
            </h5>
          </div>
        </div>
        {/* fin affichage du switch retrait depot ou livraison */}
        <div
          className={`d-flex align-items-center justify-content-between gap-2 ${
            transformedDataDepot?.length > 0 ? 'mb-4' : 'mb-3'
          }`}
        >
          <div className={'d-flex align-items-center gap-2'}>
            <i className='ki-duotone ki-delivery-geolocation fs-2x text-primary'>
              <span className='path1'></span>
              <span className='path2'></span>
              <span className='path3'></span>
              <span className='path4'></span>
              <span className='path5'></span>
            </i>
            <label className={'text-gray-800 fs-4'}>
              Adresse de {retraitDepot ? 'dépôt' : 'livraison'}
            </label>
          </div>

          <div style={{maxWidth: '200px'}}>
            {!retraitDepot && (
              <Select
                className='react-select-styled react-select-solid react-select-lg min-w-200px'
                classNamePrefix='react-select'
                fieldName='facturation'
                placeholder={'Adresse de livraison'}
                options={transformedDataLiv}
                value={livraisonSelectedValue}
                onChange={(e) => handleSelectedLivChange(e)}
                isDisabled={adresseLivraison?.length <= 1 ? 'true' : ''}
              />
            )}
          </div>
        </div>
        <div
          style={{
            maxHeight: '350px',
            overflowY: 'scroll',
          }}
        >
          {/*TransformedDataDepot affiche les adresses de retrait de dépôt
            Contient des données que seulement si retraitDepot est true
          */}
          {retraitDepot ? (
            transformedDataDepot?.length > 0 ? (
              pieces?.length > 0 ? (
                pieces?.map((pc) => {
                  const idProposition = isClientSide ? pc?.id : pc?.idPiecePropose
                  const selectedDepotId = selectedDepotsByPiece?.[idProposition]
                  const selectedDepot = adresseDepots?.find((dep) => dep?.id === selectedDepotId)
                  return (
                    <div key={pc?.id}>
                      <div
                        className={'d-flex justify-content-between align-items-center mb-3 gap-6'}
                      >
                        <div className={'d-flex gap-3'}>
                          {pc?.images?.length !== 0 ? (
                            <div>
                              <img
                                className={'rounded-circle'}
                                src={`https://koena.fr/photos/pieces/${pc?.images[0]}`}
                                alt={`${pc?.nomPieceProposee ?? pc?.nomTypePieceProposee}`}
                                width='27'
                                height='27'
                              />
                            </div>
                          ) : (
                            <ImgPieceBlank width={27} height={27} />
                          )}
                          <span className={'badge badge-light-info'}>
                            {pc?.nomPieceProposee ?? pc?.nomTypePieceProposee}
                          </span>
                        </div>
                        <div className={''} style={{maxWidth: '200px'}}>
                          <Select
                            className='react-select-styled react-select-solid react-select-lg min-w-200px'
                            classNamePrefix='react-select'
                            fieldName='depot'
                            placeholder='Adresse de depôt'
                            options={transformedDataDepot}
                            value={
                              transformedDataDepot?.find((opt) => opt.value === selectedDepotId) ??
                              null
                            }
                            onChange={(e) =>
                              handleSelectedDepotPiece(
                                e?.value,
                                isClientSide ? pc?.id : pc?.idPiecePropose
                              )
                            }
                            isDisabled={adresseDepots?.length <= 1 ? 'true' : ''}
                            // Ajout de z-index pour afficher le sélécteur sans overflow
                            menuPortalTarget={document.body}
                            menuPosition='fixed'
                            styles={{
                              menuPortal: (base) => ({...base, zIndex: 9999}),
                            }}
                          />
                        </div>
                      </div>
                      <div className={'mb-4'}>
                        <div className={'ms-3 border rounded p-2 ps-3'}>
                          {selectedDepot ? (
                            <>
                              <span className={'d-block text-muted'}>{selectedDepot?.nom}</span>
                              <span className={'d-block text-muted'}>{selectedDepot?.adresse}</span>
                              <span className={'d-block text-muted'}>
                                {selectedDepot?.ville} {selectedDepot?.cp}
                              </span>
                              <span className={'d-block text-muted'}>France</span>
                            </>
                          ) : (
                            <span className={'d-block text-muted fst-italic'}>
                              Aucune adresse sélectionnée
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              ) : (
                <div
                  className={
                    'mb-3 border rounded p-2 align-items-center mr-3 text-center text-muted w-100'
                  }
                >
                  Aucune pièce sélectionnée
                </div>
              )
            ) : (
              <>
                <div
                  className={
                    'mb-3 border rounded p-2 align-items-center mr-3 text-center text-muted w-100'
                  }
                >
                  Aucune adresse de retrait dépôt trouvée
                </div>
              </>
            )
          ) : (
            <>
              <div className={'ms-3 border rounded p-2 ps-3'}>
                <span className={'d-block text-muted fs-bold'}>
                  <span className='d-block text-muted fs-bold'>
                    {dataClient?.professionnel
                      ? dataClient?.nomSociete
                      : [dataClient?.nom, dataClient?.prenom].filter(Boolean).join(' ')}
                  </span>
                </span>
                <span className={'d-block text-muted'}>{updatedAdresseLivInfo?.adresseLiv}</span>
                <span className={'d-block text-muted'}>
                  {updatedAdresseLivInfo?.complementAdresseLiv}
                </span>
                <span className={'d-block text-muted'}>
                  {updatedAdresseLivInfo?.cPLiv} {updatedAdresseLivInfo?.villeLiv}
                </span>
                <span className={'d-block text-muted'}>{updatedAdresseLivInfo?.paysLiv}</span>
              </div>
            </>
          )}
        </div>
        {retraitDepot && (
          <div className='text-info text-left text-end w-100 fs-6'>
            Attention, délai moyen de 2 jours ouvrés avant disponibilité
          </div>
        )}
        {/* Début - Commentaire interne */}
        <div
          className={`d-flex align-items-center justify-content-between gap-2 mt-3 ${
            transformedDataDepot?.length > 0 ? 'mb-4' : 'mb-3'
          }`}
        >
          <div className={'d-flex align-items-center gap-2'}>
            <div>
              <span className='ki-duotone ki-pencil fs-2x text-primary'>
                <span className='path1'></span>
                <span className='path2'></span>
              </span>
            </div>
            <label className={'text-gray-800 fs-4'}>Commentaire interne</label>
            <div
              className='mt-2 ms-2'
              data-tooltip-id='tooltip'
              data-tooltip-content={
                'Indiquez vos commentaires liés à la préparation de commande si besoin (60 caractères maximum)'
              }
              data-tooltip-place='top'
            >
              <span className='ki-duotone ki-question-2 fs-5'>
                <span className='path1'></span>
                <span className='path2'></span>
                <span className='path3'></span>
              </span>
            </div>
          </div>
        </div>

        <div className='ps-3'>
          <textarea
            name='message'
            className='form-control border'
            onChange={(e) => setCommentaireInterne(e?.target?.value ?? '')}
            value={commentaireInterne}
          />
        </div>
        {/* Fin - Commentaire interne */}
      </>
    )
  }

  const handleFormToDisplay = (event: React.ChangeEvent<HTMLInputElement>) => {
    appliquerMode(event.target.value)
  }

  const appliquerMode = (mode: string) => {
    setSelectedOption?.(mode)
    // Seule la CB encaisse dans le modal : les deux autres modes valident la commande.
    setBtnSubmitText(mode === MODE_CB ? 'Payer' : 'Valider')
  }

  const renderCgv = () => {
    return (
      <>
        <div className={'mt-6'}>
          <div className='form-check form-check-custom form-check-solid form-check-sm'>
            <input
              className='form-check-input'
              type='checkbox'
              checked={cgvChecked}
              id='flexCheckChecked'
              onChange={() => setCgvChecked(!cgvChecked)}
            />
            <label className='form-check-label fs-6' htmlFor='flexCheckChecked'>
              Je reconnais avoir pris connaissance et accepter les{' '}
              <a
                href={getLink(source)}
                target='_blank'
                rel='noopener noreferrer'
                className='text-primary text-decoration-underline'
              >
                Conditions Générales de Vente
              </a>
            </label>
          </div>
        </div>
      </>
    )
  }

  const renderForm = (mode: string) => {
    if (mode === MODE_CB) {
      return renderStripe()
    }

    if (mode === MODE_VIREMENT) {
      return (
        <div className={'alert alert-light-primary py-3 px-3 mb-0 fs-7'} role='alert'>
          Votre commande est validée dès maintenant. Les coordonnées bancaires et la référence à
          rappeler vous seront transmises avec votre facture ; la marchandise est expédiée à
          réception du virement.
        </div>
      )
    }

    if (mode === MODE_EN_COMPTE) {
      return (
        <div className={'alert alert-light-primary py-3 px-3 mb-0 fs-7'} role='alert'>
          Le montant sera porté sur votre compte client et réglé selon les conditions habituelles.
        </div>
      )
    }

    return null
  }

  // Bloc de paiement CB : un PaymentIntent par société concernée par le panier.
  const renderStripe = () => {
    if (erreurPaiement) {
      return (
        <div className={'alert alert-danger py-2 px-3 fs-7 mb-0'} role='alert'>
          {erreurPaiement}
          <button
            type={'button'}
            className={'btn btn-sm btn-light-danger ms-3'}
            onClick={() => chargerPaiements()}
          >
            Réessayer
          </button>
        </div>
      )
    }

    // Tant que les PaymentIntent ne sont pas revenus, on reste en préparation :
    // afficher « aucun montant » ici serait trompeur.
    if (paiements.length === 0) {
      return (
        <div className={'text-muted fs-7 py-3'}>
          <span className='spinner-border spinner-border-sm align-middle me-2'></span>
          Préparation du paiement…
        </div>
      )
    }

    return (
      <StripePaiementStepper
        paiements={paiements}
        payerRef={payerRef}
        setEnCours={setPaiementEnCours}
        onTousPayesChange={setTousPayes}
        onError={(message) => setErreurPaiement(message)}
        onAllPaid={(paymentIntentIds) => onPaiementReussi?.(paymentIntentIds)}
      />
    )
  }

  // Règles métier :
  //   - CB Stripe et virement bancaire sont proposés dans tous les cas ;
  //   - « validation en compte » uniquement si le client est en compte ET non bloqué.
  // Le backend applique les mêmes règles (l'endpoint de validation est public).
  const renderPaymentSection = () => {
    return (
      <>
        <div className={'fs-4 mb-2 text-gray-800 mb-3'}>Paiement</div>
        <div className={'border rounded p-3'}>
          {modesDisponibles.map((mode, index) => (
            <div key={mode.value} className={index < modesDisponibles.length - 1 ? 'mb-4' : ''}>
              <div className='form-check form-check-custom form-check-solid form-check-sm'>
                <input
                  className='form-check-input'
                  type='radio'
                  value={mode.value}
                  id={mode.value}
                  name={'paiement'}
                  checked={selectedOption === mode.value}
                  onChange={handleFormToDisplay}
                />
                <label className='form-check-label cursor-pointer' htmlFor={mode.value}>
                  {mode.label}
                </label>
              </div>
            </div>
          ))}
          <div className={'mt-4'}>{renderForm(selectedOption)}</div>
        </div>
      </>
    )
  }

  // Seul le règlement CB encaisse depuis le modal ; virement et validation en
  // compte valident directement la commande.
  const paiementCbRequis = isClientSide && selectedOption === MODE_CB

  // Déclenche la confirmation Stripe de l'étape courante depuis le footer.
  const handleFooterClick = () => {
    if (!payerRef.current) {
      setErreurPaiement("Le formulaire de paiement n'est pas encore prêt.")
      return
    }
    payerRef.current()
  }

  return (
    <div className='modal fade' tabIndex={-1} id='modal_payment'>
      <div className='modal-dialog' style={{maxWidth: '80vw'}}>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Paiement</h5>
            {isClientSide && (
              <button
                type='button'
                className='btn btn-sm btn-primary'
                data-bs-toggle='modal'
                data-bs-target='#modal_client_demande_message'
              >
                <KTSVG
                  path='/media/icons/duotune/communication/com012.svg'
                  className='svg-icon-1'
                />
                <span className='mx-2'>Demander à être rappelé</span>
              </button>
            )}
            <div
              className='btn btn-icon btn-sm btn-active-light-primary ms-2'
              data-bs-dismiss='modal'
              aria-label='Close'
            >
              <KTSVG
                path='/media/icons/duotune/arrows/arr061.svg'
                className='svg-icon svg-icon-2x'
              />
            </div>
          </div>
          <div className='modal-body'>
            <div className={'row'}>
              <div className={'col'}>
                <div className={'fs-4 mb-3 text-gray-800 mb-3'}>Liste des articles commandés</div>
                <div
                  style={{
                    overflowY: 'scroll',
                    overflowX: 'hidden',
                    maxHeight: '500px',
                  }}
                >
                  {renderPieceList()}
                </div>
                <hr />
                <div className={'d-flex align-items-center justify-content-between'}>
                  <span className={'fs-5'}>
                    {retraitDepot ? 'Retrait dépôt SURPLUS' : 'Livraison standard domicile'}
                  </span>
                  {transportAPayer == 0 ? (
                    <span className={'text-primary'}>Offert</span>
                  ) : (
                    <span>
                      {transportAPayer?.toFixed(2)} € TTC
                    </span>
                  )}
                </div>
                <hr />
                <div className={'d-flex align-items-center justify-content-between'}>
                  <span className={'fs-5 fw-bold'}>
                    Montant commande total :
                  </span>
                  <span>
                    {montantAPayer.toFixed(2) ?? 0} € TTC
                  </span>
                </div>
              </div>
              <div className={'col'}>
                {isClientSide ? (
                  facturationSelectedValue?.value == null &&
                  livraisonSelectedValue?.value == null ? (
                    <span
                      className={
                        'd-flex h-100 align-items-center text-center mx-3 text-warning fs-5'
                      }
                    >
                      Veuillez contacter les télévendeurs pour renseigner les adresses manquantes
                    </span>
                  ) : (
                    renderAdresseList()
                  )
                ) : (
                  renderAdresseList()
                )}
              </div>
              <div className={'col fs-4 mb-3'}>
                <div>{renderPaymentSection()}</div>
                <div>{isClientSide && renderCgv()}</div>
              </div>
            </div>
          </div>
          <div className={'modal-footer'}>
            <button
              className={'btn btn-light'}
              data-bs-dismiss={`${isClientSide ? 'modal' : ''}`}
              data-bs-target={`${isClientSide ? '' : '#kt_modal_1'}`}
              data-bs-toggle={`${isClientSide ? '' : 'modal'}`}
              onClick={() => isClientSide && setCheckedPieces([])} // Purge pièce chécké
              ref={btnCancelAndCloseRef}
            >
              {isClientSide ? 'Annuler' : 'Retour'}
            </button>

            {/*Masquer le bouton si totalité des pièces est = 0*/}
            {/*Masquer le bouton si la demande est sur retrait au dépôt et qu'il n'y a pas d'adresse de dépôt*/}
            {selectedOption != '' &&
              (!retraitDepot || adresseDepots?.length > 0) && ( //à vérifier
                <button
                  className={'btn btn-primary'}
                  type={'button'}
                  // Le modal ne se ferme jamais tant qu'un paiement CB est en jeu :
                  // c'est la réussite du paiement qui déclenche la validation.
                  data-bs-dismiss={`${(isClientSide || haveNoSelectedDepot) ? '' : 'modal'}`}
                  disabled={
                    ((paiementCbRequis &&
                      (paiementEnCours || chargementPaiements || paiements.length === 0) &&
                      !cgvChecked) ||
                      !cgvChecked) &&
                    location.pathname.includes('suggestion')
                  }
                  onClick={() => {
                    if (haveNoSelectedDepot) {
                      toast.error('Veuillez sélectionner un point de dépôt')
                      return
                    }

                    if (isClientSide && paiementCbRequis) handleFooterClick()
                    else onValidate()
                  }}
                >
                  {paiementEnCours && (
                    <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                  )}
                  {paiementCbRequis && tousPayes ? (
                    'Finaliser la commande'
                  ) : (
                    <>
                      {btnSubmitText}
                      {btnSubmitText !== 'Valider' && <> {montantAPayer.toFixed(2)} €</>}
                    </>
                  )}
                </button>
              )}
          </div>
        </div>
      </div>
      <Tooltip id='tooltip' />
    </div>
  )
}

export default PaymentResumeModal
